import type { CenterProps, IntrinsicSystemProps } from '@hover/blueprint';
import { Center, forwardRef } from '@hover/blueprint';
import { assertUnreachable } from '@hover/utilities';
import React from 'react';

import { altStyle } from '../../helpers';
import { useHomeownerTranslate } from '../../hooks';
import type { DesignerProduct } from '../../types';

export interface ProductSwatchProps extends CenterProps {
  /**
   * Whether to visibly display the `alt` text for material products.
   *
   * @defaultValue false
   *
   * @remarks Material products will always have accessible `alt` text, but
   * because these swatches tend to be so small, it's usually pretty silly.
   */
  displayAltText?: boolean;
  product: DesignerProduct;
  children?: never;
}

const useSwatchProps = (product: DesignerProduct) => {
  const t = useHomeownerTranslate();

  const { primaryLabel, secondaryLabel } = product;

  switch (product.$type) {
    case 'color':
      return { bg: product.hex } satisfies CenterProps;
    case 'material':
      return {
        as: 'img',
        src: product.thumbnail,
        alt: secondaryLabel
          ? t('materialCallouts.productSwatch.primarySecondaryProductAlt', {
              primaryLabel,
              secondaryLabel,
            })
          : t('materialCallouts.productSwatch.primaryProductAlt', {
              primaryLabel,
            }),
      } satisfies IntrinsicSystemProps<'img'>;
    default:
      return assertUnreachable(product);
  }
};

export const ProductSwatch = forwardRef<ProductSwatchProps, 'div'>(
  ({ displayAltText = false, product, sx, ...props }, ref) => (
    <Center
      bg="neutral.700"
      borderRadius="300"
      display="block"
      height="500"
      position="relative"
      width="500"
      {...useSwatchProps(product)}
      sx={{
        ...altStyle('body200', {
          color: 'neutral.500',
          hide: !displayAltText,
        }),
        ...sx,
      }}
      {...props}
      ref={ref}
    />
  ),
);
